import { ParsedUrlQuery } from 'querystring'
import { GiveawaySchedule } from '@/services/Theatrical/queries'
import { SketchGuildBanner } from '../../ShowtimesView/components/banners/SketchGuildBanner'
import { promos } from './config'
import { Item, TheatricalPromotion, TheatricalPromotionType } from './types'

const GIVEAWAY_BANNER_ENABLED = false

export const getPromo = (theatricalSlug: string) => {
  return promos.find((promo) => promo.theatricalSlug === theatricalSlug) ?? null
}

const resolveBanner = (banner: Item['banner']): Item['banner'] | null => {
  if (!banner) return null
  if (banner.bannerType === 'custom_image' && !!banner.desktopImage && !!banner.tabletImage && !!banner.mobileImage) {
    return {
      bannerType: 'custom_image',
      desktopImage: banner.desktopImage,
      tabletImage: banner.tabletImage,
      mobileImage: banner.mobileImage,
    }
  }
  if (
    banner.bannerType === 'early_bird' &&
    !!banner.backgroundImage &&
    !!banner.foregroundImage &&
    !!banner.accentColor &&
    !!banner.openingWeek
  ) {
    return {
      bannerType: 'early_bird',
      backgroundImage: banner.backgroundImage,
      foregroundImage: banner.foregroundImage,
      accentColor: banner.accentColor,
      openingWeek: banner.openingWeek,
    }
  }
  if (banner.bannerType === 'no_fees' && !!banner.backgroundImage && !!banner.foregroundImage && !!banner.accentColor) {
    return {
      bannerType: 'no_fees',
      backgroundImage: banner.backgroundImage,
      foregroundImage: banner.foregroundImage,
      accentColor: banner.accentColor,
    }
  }
  if (banner.bannerType === 'custom_code' && !!banner.name) {
    return { bannerType: 'custom_code', name: banner.name }
  }

  if (banner.bannerType === 'premium_guild') {
    return { bannerType: 'premium_guild' }
  }

  return null
}

const resolveModal = (modal: Item['modal']): Item['modal'] | null => {
  if (!modal) return null
  if (modal.modalType === 'early_bird' && !!modal.backgroundImage && !!modal.accentColor && !!modal.openingWeek) {
    return {
      modalType: 'early_bird',
      backgroundImage: modal.backgroundImage,
      accentColor: modal.accentColor,
      openingWeek: modal.openingWeek,
    }
  }
  if (modal.modalType === 'no_fees' && !!modal.backgroundImage && !!modal.accentColor) {
    return { modalType: 'no_fees', backgroundImage: modal.backgroundImage, accentColor: modal.accentColor }
  }
  return null
}

const resolvePromo = (
  promo: ReturnType<typeof getPromo> | null,
  item: NonNullable<ReturnType<typeof getPromo>>['items'][number] | null,
) => {
  if (!promo || !item) return null
  return {
    theatricalSlug: promo.theatricalSlug,
    code: item.code,
    promoType: item.promoType,
    banner: resolveBanner(item?.banner),
    modal: resolveModal(item?.modal),
  }
}

const getPromoFromCode = (theatricalSlug: string, code: string) => {
  const promo = getPromo(theatricalSlug)
  const item = promo?.items.find((item) => item.code.toLowerCase() === code.toLowerCase()) ?? null
  return resolvePromo(promo, item)
}

const getPromoFromType = (theatricalSlug: string, promoType: TheatricalPromotionType) => {
  const promo = getPromo(theatricalSlug)
  const item = promo?.items.find((item) => item.promoType === promoType) ?? null
  return resolvePromo(promo, item)
}

export const pickCurrentPromo = ({
  query,
  theatricalSlug,
  hasGuildTickets,
  earlyBirdDiscountValue,
  giveawaySchedule,
}: {
  query: ParsedUrlQuery
  theatricalSlug: string
  hasGuildTickets: boolean
  earlyBirdDiscountValue: number | null
  giveawaySchedule: GiveawaySchedule | null
}): TheatricalPromotion | null => {
  const promoQueryStr = typeof query.promo === 'string' ? query.promo : null

  // Eject early if promo is claim-free-ticket
  if (promoQueryStr === 'claim-free-ticket') {
    return {
      theatricalSlug,
      code: 'CLAIM_FREE_TICKET',
      promoType: 'claim-free-ticket',
      banner: null,
      modal: null,
    }
  }

  // Prefer promo from query
  const queryPromo = promoQueryStr ? getPromoFromCode(theatricalSlug, promoQueryStr) ?? null : null
  if (queryPromo) return queryPromo

  // Then early bird discount
  if (earlyBirdDiscountValue) {
    const earlyBirdPromo = getPromoFromType(theatricalSlug, 'early_bird')
    if (earlyBirdPromo) return earlyBirdPromo
  }

  // Then giveaway schedule
  if (giveawaySchedule && GIVEAWAY_BANNER_ENABLED) {
    return {
      theatricalSlug,
      code: 'GIVEAWAY',
      promoType: 'giveaway',
      banner: {
        bannerType: 'giveaway',
      },
      modal: null,
    }
  }

  // Then guild discounts
  if (hasGuildTickets) {
    return {
      theatricalSlug,
      code: 'PREMIUM_GUILD',
      promoType: 'premium_guild',
      banner: {
        bannerType: 'premium_guild',
      },
      modal: null,
    }
  }

  // No fees is current default
  return getPromoFromType(theatricalSlug, 'no_fees')

  // TODO: where do limited-date promos fit in?
}

const CustomCodeBannerMap = {
  SketchGuildBanner: <SketchGuildBanner />,
}

const isValidCustomCodeBannerName = (name: string): name is keyof typeof CustomCodeBannerMap => {
  return name in CustomCodeBannerMap
}

export const getCustomCodeBanner = (name: string) => {
  return isValidCustomCodeBannerName(name) ? CustomCodeBannerMap[name] : null
}
